"use client";

import { useEffect, useState } from "react";
import { Menu, ChevronDown, ExternalLink } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuTrigger, NavigationMenuContent, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const isCustomerPortal = (label?: string) => (label || "").trim().toLowerCase() === "customer portal";
  const visibleMenuItems = menuItems.filter(item => !isCustomerPortal(item.label));
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-24 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-1 xl:gap-2">
        {visibleMenuItems.map((item, i) => {
        const hasChildren = item.children && item.children.length > 0;
        const isServices = item.label === "Services";
        if (hasChildren && isServices) {
          return <NavigationMenuItem key={item.id} data-index={i}>
              <NavigationMenuTrigger className="bg-transparent hover:bg-transparent hover:text-primary text-foreground text-xs xl:text-sm font-medium font-default tracking-wider xl:tracking-widest uppercase px-2 xl:px-3 py-2 rounded-md transition-colors data-[state=open]:text-primary group">
                <Link to={item.href || "/ServicesPage"} className="hover:text-primary text-inherit transition-colors" onClick={e => e.stopPropagation()}>
                  {item.label}
                </Link>
              </NavigationMenuTrigger>
              <NavigationMenuContent className="min-w-[240px] bg-background border border-border/40 shadow-lg rounded-md p-2">
                <ul className="flex flex-col gap-1">
                  <li>
                    <NavigationMenuLink asChild>
                      <Link to="/ServicesPage#pest-control" className="block px-4 py-2 text-sm font-medium text-foreground hover:text-primary hover:bg-accent rounded-md transition-colors">
                        General Pest Control
                      </Link>
                    </NavigationMenuLink>
                  </li>
                  {item.children?.map(child => <li key={child.id}>
                      <NavigationMenuLink asChild>
                        <Link to={child.href || "#"} className="block px-4 py-2 text-sm font-medium text-foreground hover:text-primary hover:bg-accent rounded-md transition-colors">
                          {child.label}
                        </Link>
                      </NavigationMenuLink>
                    </li>)}
                  <li>
                    <NavigationMenuLink asChild>
                      <Link to="/ServicesPage#entry-point-treatments" className="block px-4 py-2 text-sm font-medium text-foreground hover:text-primary hover:bg-accent rounded-md transition-colors">
                        Entry Point Treatments
                      </Link>
                    </NavigationMenuLink>
                  </li>
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>;
        }
        return <NavigationMenuItem key={item.id} data-index={i}>
              {hasChildren ? <>
                  <NavigationMenuTrigger className="bg-transparent hover:bg-transparent hover:text-primary text-foreground text-xs xl:text-sm font-medium font-default tracking-wider xl:tracking-widest uppercase px-2 xl:px-3 py-2 rounded-md transition-colors data-[state=open]:text-primary">
                    {item.label}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="min-w-[200px] bg-background border border-border/40 shadow-lg rounded-md p-2">
                    <ul className="flex flex-col gap-1">
                      {item.children?.map(child => <li key={child.id}>
                          <NavigationMenuLink asChild>
                            <Link to={child.href || "#"} className="block px-4 py-2 text-sm font-medium text-foreground hover:text-primary hover:bg-accent rounded-md transition-colors">
                              {child.label}
                            </Link>
                          </NavigationMenuLink>
                        </li>)}
                    </ul>
                  </NavigationMenuContent>
                </> : <NavigationMenuLink asChild>
                  <Link to={item.href || "#"} className="relative text-foreground text-xs xl:text-sm font-medium font-default tracking-wider xl:tracking-widest uppercase px-2 xl:px-3 py-2 rounded-md transition-colors hover:bg-transparent hover:text-primary group bg-transparent">
                    <span className="relative">
                      {item.label}
                      <span className="pointer-events-none absolute left-0 -bottom-1 h-[1px] w-0 transition-all duration-300 group-hover:w-full bg-primary" />
                    </span>
                  </Link>
                </NavigationMenuLink>}
            </NavigationMenuItem>;
      })}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const isCustomerPortal = (label?: string) => (label || "").trim().toLowerCase() === "customer portal";
  const visibleMenuItems = menuItems.filter(item => !isCustomerPortal(item.label));
  const toggleExpand = (id: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  if (loading) {
    return <div className="flex flex-col gap-3 px-6">
        <div className="h-10 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-full bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-1 px-6">
      {visibleMenuItems.map((item, i) => {
      const hasChildren = item.children && item.children.length > 0;
      const isExpanded = expandedItems[item.id];
      const isServices = item.label === "Services";
      if (hasChildren && isServices) {
        return <div key={item.id} data-index={i} className="flex flex-col border-b border-border/40">
            <div className="flex items-center justify-between w-full py-4">
              <Link to={item.href || "/ServicesPage"} onClick={onNavigate} className="text-foreground text-lg sm:text-xl font-semibold font-default transition-colors hover:text-primary text-left flex-1">
                {item.label}
              </Link>
              <Button variant="ghost" size="icon" onClick={() => toggleExpand(item.id)} aria-label={`Toggle ${item.label} sub-menu`} className="text-foreground hover:text-primary hover:bg-transparent p-0 h-auto w-auto transition-colors">
                <ChevronDown className={cn("size-6 transition-transform duration-200", isExpanded && "rotate-180")} />
              </Button>
            </div>
            {isExpanded && <div className="flex flex-col gap-1 pl-4 pb-3">
                <Link to="/ServicesPage#pest-control" onClick={onNavigate} className="text-foreground/80 text-xl font-medium font-default py-2 transition-colors hover:text-primary">
                  General Pest Control
                </Link>
                {item.children?.map(child => <Link key={child.id} to={child.href || "#"} onClick={onNavigate} className="text-foreground/80 text-xl font-medium font-default py-2 transition-colors hover:text-primary">
                    {child.label}
                  </Link>)}
                <Link to="/ServicesPage#entry-point-treatments" onClick={onNavigate} className="text-foreground/80 text-xl font-medium font-default py-2 transition-colors hover:text-primary">
                  Entry Point Treatments
                </Link>
              </div>}
          </div>;
      }
      return <div key={item.id} data-index={i} className="flex flex-col border-b border-border/40">
            {hasChildren ? <>
                <Button variant="ghost" onClick={() => toggleExpand(item.id)} className="flex items-center justify-between w-full text-foreground text-lg sm:text-xl font-semibold font-default py-4 h-auto px-0 transition-colors hover:text-primary hover:bg-transparent text-left">
                  {item.label}
                  <ChevronDown className={cn("size-6 transition-transform duration-200", isExpanded && "rotate-180")} />
                </Button>
                {isExpanded && <div className="flex flex-col gap-1 pl-4 pb-3">
                    {item.children?.map(child => <Link key={child.id} to={child.href || "#"} onClick={onNavigate} className="text-foreground/80 text-xl font-medium font-default py-2 transition-colors hover:text-primary">
                        {child.label}
                      </Link>)}
                  </div>}
              </> : <Link to={item.href || "#"} onClick={onNavigate} className="text-foreground text-lg sm:text-xl font-semibold font-default py-4 transition-colors hover:text-primary">
                {item.label}
              </Link>}
          </div>;
    })}
    </nav>;
}
export default function MainNavigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section data-section-id="115" id="main-navigation" className="sticky top-0 left-0 right-0 z-50 w-full bg-background text-foreground">
      <div className={"w-full transition-all duration-300 " + (scrolled ? "bg-background/80 backdrop-blur-xl border-b border-border/40" : "bg-background")}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-[4.5rem] items-center justify-between">
            <div className="flex flex-col justify-center">
              <Link to="/" className="relative text-foreground text-xs xl:text-sm font-medium font-default tracking-wider xl:tracking-widest uppercase px-2 xl:px-3 py-2 rounded-md transition-colors hover:bg-transparent hover:text-primary group bg-transparent min-h-[44px] min-w-[44px] flex items-center justify-center" aria-label="Attic to Basement Home">
                <span className="relative">
                  Home
                  <span className="pointer-events-none absolute left-0 -bottom-1 h-[1px] w-0 transition-all duration-300 group-hover:w-full bg-primary" />
                </span>
              </Link>
            </div>

            <div className="hidden lg:flex items-center justify-center flex-1">
              <MenuProvider menu_id="37">
                <DesktopMenuConsumer />
              </MenuProvider>
            </div>

            <div className="hidden lg:flex items-center gap-2 xl:gap-3">
              <Button asChild variant="outline" className="rounded-md border border-primary text-primary bg-transparent hover:bg-primary hover:text-primary-foreground px-3 xl:px-5 h-10 xl:h-11 text-xs xl:text-sm font-bold font-default tracking-wider xl:tracking-widest uppercase transition-all duration-300 shadow-sm">
                <Link href="https://client.gorilladesk.com/attictobasementpestcontrol" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5">
                  <span>Customer Portal</span>
                  <ExternalLink className="size-3.5 xl:size-4" />
                </Link>
              </Button>
              <Button asChild className="rounded-md px-3 xl:px-6 h-10 xl:h-11 text-xs xl:text-sm font-bold font-default tracking-wider xl:tracking-widest uppercase text-primary-foreground bg-primary hover:bg-secondary hover:text-primary transition-all duration-300 shadow-md">
                <Link to="/ContactPage">Free Inspection</Link>
              </Button>
            </div>

            <div className="flex lg:hidden items-center">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" className="text-foreground rounded-md hover:bg-accent min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="Open menu">
                    <Menu className="size-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-full sm:max-w-full bg-background border-l border-border/40 p-0 flex flex-col">
                  <SheetHeader className="px-6 pt-6 pb-4">
                    <SheetTitle className="text-left">
                      <Link to="/" onClick={() => setMobileOpen(false)} className="text-foreground text-xl sm:text-2xl font-semibold font-default tracking-wide transition-colors hover:text-primary min-h-[44px] inline-flex items-center" aria-label="Attic to Basement Home">
                        Home
                      </Link>
                    </SheetTitle>
                  </SheetHeader>

                  <div className="flex-1 overflow-y-auto pt-6">
                    <MenuProvider menu_id="37">
                      <MobileMenuConsumer onNavigate={() => setMobileOpen(false)} />
                    </MenuProvider>
                  </div>

                  <div className="px-6 pb-8 pt-6 border-t border-border/40 flex flex-col gap-4">
                    <Link to="tel:9198961953" onClick={() => setMobileOpen(false)} className="text-foreground text-xs sm:text-sm lg:text-base font-medium font-default text-center py-4 min-h-[44px] flex items-center justify-center rounded-md border border-border transition-colors hover:bg-accent">
                      919-896-1953
                    </Link>
                    <Button asChild variant="outline" className="w-full rounded-md border border-primary text-primary bg-transparent hover:bg-primary hover:text-primary-foreground h-14 text-base font-bold font-default tracking-widest uppercase transition-all duration-300 shadow-sm">
                      <Link href="https://client.gorilladesk.com/attictobasementpestcontrol" target="_blank" rel="noopener noreferrer" onClick={() => setMobileOpen(false)} className="inline-flex items-center justify-center gap-2">
                        <span>Customer Portal</span>
                        <ExternalLink className="size-4" />
                      </Link>
                    </Button>
                    <Button asChild className="rounded-md h-14 text-base font-bold font-default tracking-widest uppercase text-primary-foreground bg-primary hover:bg-secondary transition-all duration-300 shadow-lg">
                      <Link to="/ContactPage" onClick={() => setMobileOpen(false)}>
                        Free Inspection
                      </Link>
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>

        <div className={"h-[1px] w-full transition-opacity duration-500 " + (scrolled ? "opacity-100" : "opacity-0")} style={{
        background: "linear-gradient(90deg, transparent, var(--primary), transparent)"
      }} />
      </div>
    </section>;
}